// progressTracker.js - Utility functions for tracking user progress

import { supabase } from '../supabaseClient';
import { checkBadges } from './badgeEngine';

// Scroll order for progression (identifier strings, in sequence)
const SCROLL_PROGRESSION = [
  'habit',
  'love',
  'persistence',
  'selfEsteem',
  'presence',
  'emotionalMastery',
  'joy',
  'growth',
  'action',
  'wisdom',
  'confidence',
  'discipline',
  'authenticity',
  'integrity',
];

/**
 * Helper: today's date in local timezone as YYYY-MM-DD
 * If before 3:30 AM, uses yesterday's date
 */
export const getLocalDateString = () => {
  const now = new Date();
  if (now.getHours() < 3 || (now.getHours() === 3 && now.getMinutes() < 30)) {
    now.setDate(now.getDate() - 1);
  }
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * The single source of truth for the "Day X of 30" display number.
 * qualifying_days only reflects days that have fully rolled over (past the
 * 3:30 AM local cutoff). While last_reading_date is still today, today's day is "in
 * progress" and hasn't been banked yet, so we show qualifying_days as-is.
 * Once last_reading_date is no longer today (or there's no reading at all yet, i.e.
 * last_reading_date is null), the in-progress day is the next one — qualifying_days + 1.
 */
export const computeDisplayQualifyingDays = (qualifyingDays, lastDate) => {
  const today = getLocalDateString();
  return lastDate === today ? qualifyingDays : qualifyingDays + 1;
};

// ─────────────────────────────────────────────
// READING TRACKING
// ─────────────────────────────────────────────

/**
 * Track a scroll reading completion.
 * Also triggers a recalculation of scroll_progress for that scroll.
 */
export const trackScrollReading = async (scrollId, readingNumber) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.warn('Cannot track reading: User not authenticated');
      return false;
    }
    if (readingNumber < 1 || readingNumber > 3) {
      console.error('Invalid reading number. Must be 1, 2, or 3');
      return false;
    }

    const today = getLocalDateString();

    const { error } = await supabase
      .from('scroll_readings')
      .insert({
        user_id: user.id,
        scroll_id: scrollId,
        reading_number: readingNumber,
        date: today,
        completed_at: new Date().toISOString(),
      });

    if (error && error.code !== '23505') {
      console.error('Error tracking reading:', error);
      return false;
    }

    console.log(`✓ Tracked: ${scrollId} - Reading ${readingNumber}`);

    // Recalculate progress — client decides completion using local timezone
    const recalcResult = await recalculateScrollProgress(user.id, scrollId);

    // Check for newly earned badges
    const newBadges = await checkBadges();

    return { success: true, newBadges, displayQualifyingDays: recalcResult?.displayQualifyingDays };
  } catch (error) {
    console.error('Error in trackScrollReading:', error);
    return false;
  }
};

/**
 * Get today's progress for the current user (all scrolls combined).
 */
export const getTodayProgress = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { readingsComplete: 0, completedScrolls: [] };

    const today = getLocalDateString();

    const { data: readings, error } = await supabase
      .from('scroll_readings')
      .select('scroll_id, reading_number')
      .eq('user_id', user.id)
      .eq('date', today);

    if (error) throw error;

    const completedScrolls = [...new Set(readings?.map(r => r.scroll_id) || [])];

    return {
      readingsComplete: readings?.length || 0,
      completedScrolls,
      isPerfectDay: readings?.length >= 3,
    };
  } catch (error) {
    console.error('Error getting today progress:', error);
    return { readingsComplete: 0, completedScrolls: [] };
  }
};

/**
 * Get today's readings for the ACTIVE scroll only.
 * Used by the dashboard checkboxes.
 */
export const getActiveScrollTodayReadings = async (activeScrollId) => {
  try {
    if (!activeScrollId) return { readingsComplete: 0, completedReadings: [] };

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { readingsComplete: 0, completedReadings: [] };

    const today = getLocalDateString();

    const { data: readings, error } = await supabase
      .from('scroll_readings')
      .select('reading_number')
      .eq('user_id', user.id)
      .eq('scroll_id', activeScrollId)
      .eq('date', today);

    if (error) throw error;

    const completedReadings = readings?.map(r => r.reading_number) || [];

    return {
      readingsComplete: completedReadings.length,
      completedReadings,
      isPerfectDay: completedReadings.length >= 3,
    };
  } catch (error) {
    console.error('Error getting active scroll today readings:', error);
    return { readingsComplete: 0, completedReadings: [] };
  }
};

/**
 * Check if a specific scroll reading is already complete today.
 */
export const isReadingComplete = async (scrollId, readingNumber) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const today = getLocalDateString();

    const { data, error } = await supabase
      .from('scroll_readings')
      .select('id')
      .eq('user_id', user.id)
      .eq('scroll_id', scrollId)
      .eq('reading_number', readingNumber)
      .eq('date', today)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return !!data;
  } catch (error) {
    console.error('Error checking reading status:', error);
    return false;
  }
};

/**
 * Get all readings for a specific scroll today.
 */
export const getScrollReadingsToday = async (scrollId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const today = getLocalDateString();

    const { data: readings, error } = await supabase
      .from('scroll_readings')
      .select('reading_number')
      .eq('user_id', user.id)
      .eq('scroll_id', scrollId)
      .eq('date', today)
      .order('reading_number');

    if (error) throw error;
    return readings?.map(r => r.reading_number) || [];
  } catch (error) {
    console.error('Error getting scroll readings:', error);
    return [];
  }
};

// ─────────────────────────────────────────────
// JOURNAL
// ─────────────────────────────────────────────

export const saveJournalEntry = async (content, date = null) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const entryDate = date || getLocalDateString();

    const { data: existingEntry } = await supabase
      .from('journal_entries')
      .select('id')
      .eq('user_id', user.id)
      .eq('date', entryDate)
      .single();

    if (existingEntry) {
      const { error } = await supabase
        .from('journal_entries')
        .update({ content, updated_at: new Date().toISOString() })
        .eq('id', existingEntry.id);
      if (error) throw error;
    } else {
      const { error } = await supabase
        .from('journal_entries')
        .insert({
          user_id: user.id,
          date: entryDate,
          content,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      if (error) throw error;
    }
    return true;
  } catch (error) {
    console.error('Error saving journal entry:', error);
    return false;
  }
};

export const getJournalEntry = async (date = null) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const entryDate = date || getLocalDateString();

    const { data, error } = await supabase
      .from('journal_entries')
      .select('content')
      .eq('user_id', user.id)
      .eq('date', entryDate)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data?.content || null;
  } catch (error) {
    console.error('Error getting journal entry:', error);
    return null;
  }
};

// ─────────────────────────────────────────────
// STREAKS
// ─────────────────────────────────────────────

export const getCurrentStreak = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { currentStreak: 0, perfectStreak: 0 };

    const { data: readings, error } = await supabase
      .from('scroll_readings')
      .select('date, reading_number')
      .eq('user_id', user.id)
      .order('date', { ascending: false });

    if (error) throw error;
    if (!readings || readings.length === 0) return { currentStreak: 0, perfectStreak: 0 };

    const dailyReadings = {};
    readings.forEach(r => {
      dailyReadings[r.date] = (dailyReadings[r.date] || 0) + 1;
    });

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = getLocalDateString();

    let currentStreak = 0;
    let checkDate = new Date(today);
    if (!dailyReadings[todayStr]) checkDate.setDate(checkDate.getDate() - 1);

    let streakActive = true;
    while (streakActive) {
      const y = checkDate.getFullYear();
      const m = String(checkDate.getMonth() + 1).padStart(2, '0');
      const d = String(checkDate.getDate()).padStart(2, '0');
      const dateStr = `${y}-${m}-${d}`;
      if (dailyReadings[dateStr] > 0) {
        currentStreak++;
        checkDate.setDate(checkDate.getDate() - 1);
      } else {
        streakActive = false;
      }
    }

    let perfectStreak = 0;
    checkDate = new Date(today);
    if (!dailyReadings[todayStr] || dailyReadings[todayStr] < 3) {
      checkDate.setDate(checkDate.getDate() - 1);
    }

    streakActive = true;
    while (streakActive) {
      const y = checkDate.getFullYear();
      const m = String(checkDate.getMonth() + 1).padStart(2, '0');
      const d = String(checkDate.getDate()).padStart(2, '0');
      const dateStr = `${y}-${m}-${d}`;
      if (dailyReadings[dateStr] >= 3) {
        perfectStreak++;
        checkDate.setDate(checkDate.getDate() - 1);
      } else {
        streakActive = false;
      }
    }

    return { currentStreak, perfectStreak };
  } catch (error) {
    console.error('Error calculating streak:', error);
    return { currentStreak: 0, perfectStreak: 0 };
  }
};

// ─────────────────────────────────────────────
// SCROLL PROGRESSION
// ─────────────────────────────────────────────

/**
 * Internal: recalculate qualifying_days in scroll_progress for one scroll.
 * The RPC now returns qualifying_days and last_reading_date.
 * Completion is decided HERE using the client's local timezone and 3 AM cutoff.
 * This ensures users in any timezone get the correct day boundary behavior.
 */
const recalculateScrollProgress = async (userId, scrollId) => {
  try {
    const { data, error } = await supabase.rpc('recalculate_scroll_progress', {
      p_user_id: userId,
      p_scroll_id: scrollId,
    });

    if (error) {
      console.error('Error recalculating scroll progress:', error);
      return null;
    }

    const { qualifying_days, last_reading_date, was_completed } = data;
    const today = getLocalDateString();

    // Only mark complete if:
    // 1. 30 qualifying days reached
    // 2. Not already completed
    // 3. The last qualifying day is strictly before TODAY in LOCAL time
    //    (getLocalDateString handles timezone + 3 AM cutoff for every user)
    if (qualifying_days >= 30 && !was_completed) {
      if (last_reading_date < today) {
        console.log(`✓ Scroll ${scrollId} completed — local date ${today} > last qualifying date ${last_reading_date}`);
        await supabase.rpc('complete_scroll', {
          p_user_id: userId,
          p_scroll_id: scrollId,
        });
      } else {
        console.log(`⏳ Scroll ${scrollId} has 30 qualifying days but local day has not rolled over yet — holding completion`);
      }
    }

    const displayQualifyingDays = computeDisplayQualifyingDays(qualifying_days, last_reading_date);

    return { qualifyingDays: qualifying_days, displayQualifyingDays, lastDate: last_reading_date };
  } catch (error) {
    console.error('Error in recalculateScrollProgress:', error);
    return null;
  }
};

/**
 * Catch-up check: reads the current scroll_progress row for one scroll and,
 * if it already has 30 qualifying days, isn't marked complete yet, and the
 * local day has rolled over since the last qualifying reading, marks it
 * complete. This covers scrolls that hit 30 days and then sat untouched
 * overnight — no new reading needed to trigger completion, just a page load.
 */
export const checkScrollCompletion = async (scrollId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      .from('scroll_progress')
      .select('qualifying_days, last_reading_date, is_completed')
      .eq('user_id', user.id)
      .eq('scroll_id', scrollId)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    if (!data) return false;

    const today = getLocalDateString();

    if (data.qualifying_days >= 30 && !data.is_completed && data.last_reading_date < today) {
      console.log(`✓ Scroll ${scrollId} completed on catch-up check — local date ${today} > last qualifying date ${data.last_reading_date}`);
      await supabase.rpc('complete_scroll', {
        p_user_id: user.id,
        p_scroll_id: scrollId,
      });
      return true;
    }

    return false;
  } catch (error) {
    console.error('Error in checkScrollCompletion:', error);
    return false;
  }
};

/**
 * Get progress data for ALL scrolls for the current user.
 * Returns a map: { [scrollIdentifier]: { qualifyingDays, isCompleted, isActive } }
 */
export const getAllScrollProgress = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return {};

    const { data, error } = await supabase
      .from('scroll_progress')
      .select('scroll_id, qualifying_days, last_reading_date, is_completed, is_active, completed_at, started_at')
      .eq('user_id', user.id);

    if (error) throw error;

    const progressMap = {};
    data?.forEach(row => {
      progressMap[row.scroll_id] = {
        qualifyingDays: row.qualifying_days,
        displayQualifyingDays: computeDisplayQualifyingDays(row.qualifying_days, row.last_reading_date),
        isCompleted: row.is_completed,
        isActive: row.is_active,
        completedAt: row.completed_at,
        startedAt: row.started_at,
      };
    });

    return progressMap;
  } catch (error) {
    console.error('Error getting all scroll progress:', error);
    return {};
  }
};

/**
 * Get the progress record for a single scroll.
 */
export const getScrollProgress = async (scrollIdentifier) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('scroll_progress')
      .select('qualifying_days, last_reading_date, is_completed, is_active, completed_at, started_at')
      .eq('user_id', user.id)
      .eq('scroll_id', scrollIdentifier)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    if (!data) return { qualifyingDays: 0, displayQualifyingDays: 1, isCompleted: false, isActive: false };

    return {
      qualifyingDays: data.qualifying_days,
      displayQualifyingDays: computeDisplayQualifyingDays(data.qualifying_days, data.last_reading_date),
      isCompleted: data.is_completed,
      isActive: data.is_active,
      completedAt: data.completed_at,
      startedAt: data.started_at,
    };
  } catch (error) {
    console.error('Error getting scroll progress:', error);
    return { qualifyingDays: 0, isCompleted: false, isActive: false };
  }
};

/**
 * Ensure a scroll_progress row exists when a user starts reading a scroll.
 * Does NOT mark as active — active is managed by the RPC and promoteNextScroll.
 */
export const ensureScrollProgressStarted = async (scrollIdentifier, scrollDbId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data: existing } = await supabase
      .from('scroll_progress')
      .select('id')
      .eq('user_id', user.id)
      .eq('scroll_id', scrollIdentifier)
      .single();

    if (existing) return true;

    const { error } = await supabase
      .from('scroll_progress')
      .insert({
        user_id: user.id,
        scroll_id: scrollIdentifier,
        scroll_db_id: scrollDbId,
        qualifying_days: 0,
        is_active: false,
        is_completed: false,
      });

    if (error && error.code !== '23505') {
      console.error('Error creating scroll progress:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in ensureScrollProgressStarted:', error);
    return false;
  }
};

/**
 * Get the current active scroll identifier.
 * Reads from user_profiles.current_scroll_id.
 * Falls back to getSuggestedActiveScroll() if not set.
 */
export const getActiveScrollId = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: profile } = await supabase
      .from('user_profiles')
      .select('current_scroll_id')
      .eq('id', user.id)
      .single();

    if (profile?.current_scroll_id) {
      const { data: scroll } = await supabase
        .from('scrolls')
        .select('identifier')
        .eq('id', profile.current_scroll_id)
        .single();

      if (scroll?.identifier) {
        const { data: progress } = await supabase
          .from('scroll_progress')
          .select('is_completed')
          .eq('user_id', user.id)
          .eq('scroll_id', scroll.identifier)
          .single();

        if (!progress?.is_completed) {
          return scroll.identifier;
        }
      }
    }

    return await getSuggestedActiveScroll();
  } catch (error) {
    console.error('Error getting active scroll:', error);
    return null;
  }
};

/**
 * Determine the next suggested scroll for a user.
 * Returns the first incomplete scroll in SCROLL_PROGRESSION order.
 */
export const getSuggestedActiveScroll = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return 'habit';

    const { data, error } = await supabase
      .from('scroll_progress')
      .select('scroll_id, is_completed')
      .eq('user_id', user.id);

    if (error) throw error;
    if (!data || data.length === 0) return 'habit';

    const progressMap = {};
    data.forEach(row => { progressMap[row.scroll_id] = row; });

    for (const id of SCROLL_PROGRESSION) {
      if (!progressMap[id] || !progressMap[id].is_completed) {
        return id;
      }
    }

    return SCROLL_PROGRESSION[SCROLL_PROGRESSION.length - 1];
  } catch (error) {
    console.error('Error getting suggested active scroll:', error);
    return 'habit';
  }
};

/**
 * Check if the Habit scroll is completed (30/30).
 */
export const isHabitComplete = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      .from('scroll_progress')
      .select('is_completed')
      .eq('user_id', user.id)
      .eq('scroll_id', 'habit')
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data?.is_completed || false;
  } catch (error) {
    console.error('Error checking Habit completion:', error);
    return false;
  }
};

/**
 * Check if a user is brand new (no scroll_progress rows at all).
 */
export const isNewUser = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return true;

    const { data, error } = await supabase
      .from('scroll_progress')
      .select('id')
      .eq('user_id', user.id)
      .limit(1);

    if (error) throw error;
    return !data || data.length === 0;
  } catch (error) {
    console.error('Error checking new user status:', error);
    return false;
  }
};

/**
 * Switch the active scroll to a new one manually.
 * Only available after Habit is complete.
 * Resets the target scroll's progress to Day 0.
 */
export const switchActiveScroll = async (newScrollIdentifier, newScrollDbId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const habitDone = await isHabitComplete();
    if (!habitDone) {
      console.warn('Cannot switch scrolls: Habit scroll not yet complete');
      return false;
    }

    await supabase
      .from('scroll_progress')
      .update({ is_active: false })
      .eq('user_id', user.id);

    await supabase
      .from('scroll_readings')
      .delete()
      .eq('user_id', user.id)
      .eq('scroll_id', newScrollIdentifier);

    const { data: existing } = await supabase
      .from('scroll_progress')
      .select('id')
      .eq('user_id', user.id)
      .eq('scroll_id', newScrollIdentifier)
      .single();

    if (existing) {
      await supabase
        .from('scroll_progress')
        .update({
          qualifying_days: 0,
          is_completed: false,
          is_active: true,
          started_at: new Date().toISOString(),
          completed_at: null,
        })
        .eq('id', existing.id);
    } else {
      await supabase
        .from('scroll_progress')
        .insert({
          user_id: user.id,
          scroll_id: newScrollIdentifier,
          scroll_db_id: newScrollDbId,
          qualifying_days: 0,
          is_completed: false,
          is_active: true,
        });
    }

    await supabase
      .from('user_profiles')
      .update({ current_scroll_id: newScrollDbId })
      .eq('id', user.id);

    console.log(`✓ Switched active scroll to: ${newScrollIdentifier}`);
    return true;
  } catch (error) {
    console.error('Error switching active scroll:', error);
    return false;
  }
};

export default {
  trackScrollReading,
  getTodayProgress,
  getActiveScrollTodayReadings,
  isReadingComplete,
  getScrollReadingsToday,
  saveJournalEntry,
  getJournalEntry,
  getCurrentStreak,
  getAllScrollProgress,
  getScrollProgress,
  ensureScrollProgressStarted,
  getSuggestedActiveScroll,
  isNewUser,
  isHabitComplete,
  getActiveScrollId,
  switchActiveScroll,
};